// The desktop dmg download URL + click tracking — extracted from
// PricingSection.tsx so CTA components (DownloadCta, the installing page) can
// import them without a circular import through the pricing UI. PricingSection
// re-exports both, so every pre-existing import keeps working unchanged.

import { trackEvent } from "@/utils/mixpanel";
import { pushDataLayerEvent } from "@/utils/googleAnalytics";
import { downloadQueryString, getMetaCookies } from "@/lib/attribution";

import { marketingConsentGranted } from "@/lib/marketingConsent";
import { supabase } from "@/integrations/supabase/client";
import { createDownloadConversionQueue, downloadSourceUrl } from "@/lib/downloadConversion";

let conversionQueue: ReturnType<typeof createDownloadConversionQueue> | undefined;
function getConversionQueue() {
  if (!conversionQueue && typeof window !== "undefined") {
    conversionQueue = createDownloadConversionQueue({
      storage: window.sessionStorage,
      consent: marketingConsentGranted,
      now: Date.now,
      send: async body => {
        const { data, error } = await supabase.functions.invoke("meta-capi-download", { body, signal: AbortSignal.timeout(10_000) });
        return !error && data?.status === "accepted";
      }
    });
    window.addEventListener("online", retryDownloadConversions);
    document.addEventListener("cookieyes_consent_update", retryDownloadConversions);
  }
  return conversionQueue;
}
/** Retry real queued clicks only; visiting /installing never invents a conversion. */
export function retryDownloadConversions(): void {
  try { void getConversionQueue()?.flush(); } catch { /* Storage/analytics never blocks rendering. */ }
}

// The download goes through the `desktop-download` edge fn: it LOGS the event
// (web_extension row + `dmg_downloaded` in the desktop Mixpanel project) then
// 302s to the real notarized DMG in the `releases` bucket — every download is
// confirmable, shared/direct links included. New versions: bump the filenames in
// supabase/functions/desktop-download (vovy-ext repo) — this URL stays stable.
// Raw artifact (bypasses tracking):
//   https://swrrttvhfnsiesmkbwia.supabase.co/storage/v1/object/public/releases/Vovy-0.1.2-arm64.dmg
export const MACOS_DOWNLOAD_URL =
  "https://swrrttvhfnsiesmkbwia.supabase.co/functions/v1/desktop-download?file=arm64";

/** Web-side funnel event on the click itself (the edge fn logs the confirmed
 *  download server-side; this ties it to the visitor's web session/funnel). */
export const trackDownloadClick = (
  placement: string,
  e?: { currentTarget: HTMLAnchorElement }
): void => {
  // Stamp the anchor with attribution + identity so the edge fn can attribute
  // the download (see downloadQueryString / desktop-download's whitelist).
  //
  // WHY CLICK TIME, NOT RENDER TIME: Mixpanel init is deferred to
  // requestIdleCallback in src/App.tsx, so at first paint the visitor id often
  // doesn't exist yet. Building this URL at render time would silently drop
  // distinct_id (and auth_user_id, which arrives with the auth chokepoint).
  //
  // Always rebuilt from MACOS_DOWNLOAD_URL, never appended to the live href, so
  // a handler that somehow runs twice can't double-append params. Its own
  // try/catch, ahead of the analytics one below, so an attribution failure can
  // never suppress trackEvent/pushDataLayerEvent — and neither can block the
  // download: the untouched href in the markup is a working no-JS fallback for
  // middle-click, cmd-click, and any path where this never runs.
  try {
    const anchor = e?.currentTarget;
    if (anchor) {
      const qs = downloadQueryString(placement);
      if (qs) anchor.href = `${MACOS_DOWNLOAD_URL}&${qs}`;
    }
  } catch {
    /* attribution never blocks a download */
  }
  try { trackEvent("Desktop Download Clicked", { placement, file: "arm64" }); }
  catch { /* Product analytics must not suppress the conversion event. */ }
  const eventId = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID() : `dl-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const eventTime = Math.floor(Date.now() / 1000);
  try { sessionStorage.setItem("vovy_dl_event_id", eventId); }
  catch { /* Retain published GTM history-trigger compatibility when storage works. */ }
  try { pushDataLayerEvent("desktop_download_click", { placement, event_id: eventId }); }
  catch { /* GTM failure must not stop the server twin. */ }
  try {
    const queue = getConversionQueue();
    const source = downloadSourceUrl(window.location.href);
    if (source && marketingConsentGranted()) {
      queue?.enqueue({ event_id: eventId, event_time: eventTime, event_source_url: source,
        marketing_consent: "granted", ...getMetaCookies() });
    }
    void queue?.flush();
  } catch { /* Conversion reporting never blocks the download. */ }
};
